import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';

export interface RemoveTicketOption {
  reservationId: string;
  /** 1-based, in cart order. */
  number: number;
  /** What hangs under the ticket, as `extrasOf` reads it. */
  extras: string[];
}

export interface RemoveTicketDialogData {
  title: string;
  options: RemoveTicketOption[];
}

/**
 * Asks which ticket − takes off when the tickets of one product carry different extras (a deal, a
 * supplement): removing a ticket removes what hangs under it. Closes with the chosen `reservationId`.
 */
@Component({
  selector: 'plys-remove-ticket-dialog',
  template: `
    <div class="card card-radius m-0 relative product-options" data-test="remove-ticket-dialog">
      <button
        class="close-btn"
        type="button"
        [attr.aria-label]="'global.close' | transloco"
        tabindex="-1"
        mat-dialog-close
      >
        <mat-icon class="close-icon">close</mat-icon>
      </button>

      <h2 class="text-xl text-bold mb-2 pr-5">{{ data.title }}</h2>
      <p class="mb-4">{{ 'remove_ticket.title' | transloco }}</p>

      <div class="product-options__body">
        <fieldset class="options-step">
          @for (option of data.options; track option.reservationId) {
            <label class="options-step__option" [attr.data-test]="'remove-ticket-' + option.number">
              <input
                class="radio-control"
                type="radio"
                name="remove-ticket"
                [checked]="selected === option.reservationId"
                (change)="selected = option.reservationId"
              />
              <span class="options-step__name">
                <strong class="d-block">{{ 'remove_ticket.ticket' | transloco: { number: option.number } }}</strong>
                {{ option.extras.length ? option.extras.join(', ') : ('remove_ticket.no_extras' | transloco) }}
              </span>
            </label>
          }
        </fieldset>
      </div>

      <div class="product-options__footer">
        <button
          class="btn btn-primary product-options__primary"
          type="button"
          data-test="remove-ticket-confirm"
          [disabled]="!selected"
          (click)="ref.close(selected)"
        >
          {{ 'remove_ticket.remove' | transloco }}
        </button>
      </div>
    </div>
  `,
  styleUrls: [
    '../product-options-dialog/product-options-dialog.component.scss',
    '../product-options-dialog/product-options.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogModule, MatIconModule, TranslocoModule],
})
export class RemoveTicketDialogComponent {
  selected: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: RemoveTicketDialogData,
    readonly ref: MatDialogRef<RemoveTicketDialogComponent, string>
  ) {}
}
